import { useEffect, useMemo, useState } from 'react';
import { DEFAULT_STEP_ID, CommonStepId } from '../../../../shared/constants/stepId';
import { useOnceBeforeRenderOnClient } from '../../../../hooks/useOnceBeforeRenderOnClient';
import { GoToStepFunction, StepsContextValue } from '../../StepsContext.types';
import { MIXPANEL_STEP_TYPE_MAP } from '../../../../events/mixpanel/mixpanel.constants';
import { globalBigQueryReporter } from '../../../../events/bigQuery/bigQuery.reporter';
import { globalGrafanaReporter } from '../../../../events/grafana/grafana.reporter';
import { globalMixpanelReporter } from '../../../../events/mixpanel/mixpanel.reporter';

export const useStepsContextValue = <T extends CommonStepId>({
    stepId = DEFAULT_STEP_ID as T,
    onStepIdChange = () => null,
}: {
    stepId?: T;
    onStepIdChange?(stepId: T): void;
} = {}): StepsContextValue<T> => {
    const [stepHistory, setStepHistory] = useState<T[]>([stepId]);
    const activeStepId = stepHistory[stepHistory.length - 1] ?? DEFAULT_STEP_ID;
    const hasBack = stepHistory.length > 1;

    useOnceBeforeRenderOnClient(() => {
        const mixPanelStep = MIXPANEL_STEP_TYPE_MAP[DEFAULT_STEP_ID];

        globalMixpanelReporter.enrichContext({
            step: mixPanelStep,
        });

        globalBigQueryReporter.enrichContext({ step: DEFAULT_STEP_ID });
    });

    useEffect(() => {
        onStepIdChange(activeStepId);
    }, [activeStepId, onStepIdChange]);

    const stepsValue = useMemo(() => {
        const goToStep: GoToStepFunction<T> = (step, options) => {
            setStepHistory((prevHistory) => {
                const mixPanelStep = MIXPANEL_STEP_TYPE_MAP[step];
                if (mixPanelStep) {
                    globalMixpanelReporter.enrichContext({ step: mixPanelStep });
                }
                globalBigQueryReporter.enrichContext({ step });

                if (options?.resetHistory) {
                    return [step];
                }

                return [...prevHistory, step];
            });
        };

        const goBack = () => {
            setStepHistory((prevHistory) => {
                const newHistory = prevHistory.length <= 1 ? prevHistory : prevHistory.slice(0, -1);
                const newStep = newHistory[newHistory.length - 1];

                globalMixpanelReporter.send('Identification - Back');
                globalBigQueryReporter.send('identification.back');
                globalGrafanaReporter.count('identification.%{platform}.back');

                const mixPanelStep = MIXPANEL_STEP_TYPE_MAP[newStep];
                if (mixPanelStep) {
                    globalMixpanelReporter.enrichContext({
                        step: mixPanelStep,
                    });
                }
                globalBigQueryReporter.enrichContext({
                    step: newStep,
                });

                return newHistory;
            });
        };

        return {
            goToStep,
            goBack,
            hasBack,
            activeStepId,
        };
    }, [activeStepId, hasBack]);

    return stepsValue;
};
